import React, { useEffect, useState } from 'react';
import { DEFAULT_MODEL_PRICES, ModelPricing, setModelPriceOverrides } from '../../../lib/tokenCost';
import { settingsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';
import styles from './ModelPricingTable.module.css';

interface ModelRow {
  model: string;
  pricing: ModelPricing;
}

interface ModelPricingTableProps {
  initialJson?: string;
}

function parseJson(json?: string): ModelRow[] {
  try {
    const parsed = json ? JSON.parse(json) : {};
    const merged = { ...DEFAULT_MODEL_PRICES, ...parsed };
    return Object.entries(merged).map(([model, pricing]) => ({ model, pricing: pricing as ModelPricing }));
  } catch {
    return Object.entries(DEFAULT_MODEL_PRICES).map(([model, pricing]) => ({ model, pricing }));
  }
}

const PRICE_FIELDS: { key: keyof ModelPricing; label: string }[] = [
  { key: 'input',       label: 'Input' },
  { key: 'cacheWrite',  label: 'Cache Write (5m)' },
  { key: 'cacheWrite1h', label: 'Cache Write (1h)' },
  { key: 'cacheRead',   label: 'Cache Read' },
  { key: 'output',      label: 'Output' },
];

const ModelPricingTable: React.FC<ModelPricingTableProps> = ({ initialJson }) => {
  const [rows, setRows] = useState<ModelRow[]>(() => parseJson(initialJson));
  const [saving, setSaving] = useState(false);
  const [newModel, setNewModel] = useState('');

  useEffect(() => {
    setRows(parseJson(initialJson));
  }, [initialJson]);

  const update = (idx: number, field: keyof ModelPricing, value: string) => {
    setRows(prev => prev.map((r, i) =>
      i !== idx ? r : { ...r, pricing: { ...r.pricing, [field]: parseFloat(value) || 0 } }
    ));
  };

  const addRow = () => {
    const model = newModel.trim();
    if (!model) return;
    if (rows.some(r => r.model === model)) { toast.error('Model already exists'); return; }
    setRows(prev => [...prev, { model, pricing: { input: 0, cacheWrite: 0, cacheWrite1h: 0, cacheRead: 0, output: 0 } }]);
    setNewModel('');
  };

  const removeRow = (idx: number) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    try {
      const overrides: Record<string, ModelPricing> = {};
      for (const { model, pricing } of rows) overrides[model] = pricing;
      await settingsApi.setValue('MODEL_PRICES', { value: JSON.stringify(overrides) });
      setModelPriceOverrides(overrides);
      toast.success('Model prices saved');
    } catch {
      toast.error('Failed to save model prices');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div>
          <h3 className={styles.sectionTitle}>Model Pricing</h3>
          <p className={styles.sectionDesc}>USD per million tokens. Used for cost estimates in usage reports. Override built-in defaults here.</p>
        </div>
        <button className={styles.saveBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Prices'}
        </button>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.modelCol}>Model ID</th>
            {PRICE_FIELDS.map(f => <th key={f.key} className={styles.priceCol}>{f.label} ($/MTok)</th>)}
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.model}>
              <td className={styles.modelCell}>{row.model}</td>
              {PRICE_FIELDS.map(f => (
                <td key={f.key}>
                  <input
                    type="number"
                    className={styles.priceInput}
                    value={row.pricing[f.key] ?? 0}
                    min={0}
                    step="any"
                    onChange={e => update(idx, f.key, e.target.value)}
                  />
                </td>
              ))}
              <td>
                <button className={styles.removeBtn} onClick={() => removeRow(idx)} title="Remove">×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.addRow}>
        <input
          className={styles.newModelInput}
          type="text"
          placeholder="claude-haiku-4-5-20251001"
          value={newModel}
          onChange={e => setNewModel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addRow()}
        />
        <button className={styles.addBtn} onClick={addRow}>+ Add model</button>
      </div>
    </div>
  );
};

export default ModelPricingTable;
